import { useCallback, useEffect, useRef } from 'react';
import type { UIMessage } from 'ai';

import type { ClassifierResult } from '@flintmint/core-shared/medical-note';

import { getLatestCurrentTurnMedicalClassificationMatch } from '../current-turn-assistant-messages';
import { buildClassificationCachePatch, getPersistedParsedContentHash } from '../run/completion-patch';
import { isNoteDocument, type PersistedDocument } from '../document-utils';

type ClassificationCacheState = {
  document: PersistedDocument | undefined;
  pendingResult: ClassifierResult | null;
  lastSeenMessageId: string | null;
};

const updatePendingClassificationResult = (state: ClassificationCacheState, messages: UIMessage[]): void => {
  const latestClassification = getLatestCurrentTurnMedicalClassificationMatch(messages);
  if (!latestClassification) return;
  if (latestClassification.message.id === state.lastSeenMessageId) return;

  state.lastSeenMessageId = latestClassification.message.id;
  state.pendingResult = latestClassification.classification;
};

const clearPendingClassificationResult = (state: ClassificationCacheState): void => {
  state.pendingResult = null;
};

const persistClassificationResult = ({
  document,
  classification,
  contentHash,
}: {
  document: PersistedDocument | undefined;
  classification: ClassifierResult | null;
  contentHash: string | null;
}): boolean => {
  if (!document || !isNoteDocument(document)) return false;

  const patch = buildClassificationCachePatch({ classification, contentHash });
  if (!patch) return false;

  document.incrementalPatch(patch);

  return true;
};

const persistPendingClassificationResult = (
  state: ClassificationCacheState,
  getContentHash: (document: PersistedDocument | undefined) => string | null
): boolean => {
  if (!state.pendingResult) return false;
  if (!state.document || !isNoteDocument(state.document)) return false;

  const contentHash = getContentHash(state.document);
  if (!contentHash) return false;

  const saved = persistClassificationResult({
    document: state.document,
    classification: state.pendingResult,
    contentHash,
  });

  if (saved) {
    clearPendingClassificationResult(state);
  }

  return saved;
};

export const useClassificationCache = ({
  messages,
  requestId,
  document,
}: {
  messages: UIMessage[];
  requestId: string;
  document: PersistedDocument | undefined;
}) => {
  const stateRef = useRef<ClassificationCacheState>({
    document,
    pendingResult: null,
    lastSeenMessageId: null,
  });
  stateRef.current.document = document;

  useEffect(() => {
    clearPendingClassificationResult(stateRef.current);
    stateRef.current.lastSeenMessageId = null;
  }, [requestId]);

  useEffect(() => {
    updatePendingClassificationResult(stateRef.current, messages);
  }, [messages]);

  // Called when all suggestions are resolved — saves cache with hash from persisted parsedContent.
  const handleSuggestionDecision = useCallback((contentChanged: boolean) => {
    const state = stateRef.current;
    if (!state.pendingResult) return;

    if (!contentChanged) {
      clearPendingClassificationResult(state);
      return;
    }

    persistPendingClassificationResult(state, getPersistedParsedContentHash);
  }, []);

  const reset = useCallback(() => {
    clearPendingClassificationResult(stateRef.current);
  }, []);

  return { handleSuggestionDecision, reset };
};
